import React, { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { ItemType } from '../../../constants/ItemType';
import { MediaType } from '../../../constants/MediaType';
import { ExternalSource, MainSource, Source } from '../../../constants/Source';
import { getLocale } from '../../env/getLocale';
import {
  MediaManagerSDKAnyItem,
  MediaManagerSDKDownloadResponse,
  MediaManagerSDKFile,
  MediaManagerSDKFolder,
  MediaManagerSDKResponse,
  MediaManagerSDKResponseData,
} from '../../media-manager-sdk';
import { isSiteFilesFolderInBoards } from '../../media-manager-sdk/boards';
import { getItemSourceSafe } from '../../media-manager-sdk/getAdapter';
import { getItemType, isFolder } from '../../media-manager-sdk/items';
import { isBoards, isMediaFromWix } from '../../media-manager-sdk/sources';
import { useAdapter, useGetAdapter, useGetItemAdapter } from '../useAdapter';
import {
  CREATE_NEW_FOLDER_METHODS_PATHS,
  GET_DOWNLOAD_URL_METHODS_PATHS,
  getAdapterMethod,
  LIST_METHODS_PATHS,
  SEARCH_METHODS_PATHS,
  UPDATE_METHODS_PATHS,
  useCachedUserInfoLoader,
} from './config';
import { IUseMediaManagerSDK } from './useMediaManagerSDK.types';

const useSourceForAdapter = (incomingSource: Source) => {
  const { folderId } = useParams<{ folderId: string }>();

  return isSiteFilesFolderInBoards(incomingSource, folderId) ? MainSource.SiteFiles : incomingSource;
};

export const withFolderInfoMP = (BaseComponent) => {
  const WithFolderInfoMP: FC = (props) => {
    const getAdapter = useGetAdapter();

    const folderInfoMP = useCallback(
      (folderId, source = MainSource.SiteFiles) =>
        isMediaFromWix(source)
          ? Promise.resolve<MediaManagerSDKResponseData<MediaManagerSDKFolder>>({
              data: {
                name: 'media-from-wix',
                id: folderId,
                kind: ItemType.Folder,
                source: MainSource.MediaFromWix,
                siteId: null,
              },
              responseSize: 0,
              status: 200,
              statusText: 'OK',
            })
          : isBoards(source)
          ? getAdapter(source).item.get(folderId, { correlationId: null })
          : getAdapter(source).folder.get(folderId, { correlationId: null }),
      [getAdapter],
    );

    return <BaseComponent {...props} folderInfoMP={folderInfoMP} />;
  };

  return WithFolderInfoMP;
};

export const useMediaManagerSDK = ({ source: incomingSource, currentFolder }: IUseMediaManagerSDK) => {
  const source = useSourceForAdapter(incomingSource);
  const siteMediaToken = isFolder(currentFolder) ? currentFolder.siteMediaToken : null;

  const cachedUserInfoLoader = useCachedUserInfoLoader();
  const adapter = useAdapter(source, siteMediaToken);
  const getAdapter = useGetAdapter(siteMediaToken);
  const getItemAdapter = useGetItemAdapter(siteMediaToken);

  return useMemo(
    () => ({
      publicItemsInfoMP: (itemIds) => getAdapter(MainSource.MediaFromWix).item.get(itemIds, { language: getLocale() }),
      importToSiteMP: getAdapter(ExternalSource.WixSite).items.importToSite,
      publicMediaFoldersTreeMP: getAdapter(MainSource.MediaFromWix).folders.tree,
      listItemsMP: getAdapterMethod<MediaManagerSDKResponse<MediaManagerSDKFile[]>>(
        LIST_METHODS_PATHS,
        source,
        ItemType.File,
        adapter,
      ),
      listFoldersMP: getAdapterMethod<MediaManagerSDKResponse<MediaManagerSDKFolder[]>>(
        LIST_METHODS_PATHS,
        source,
        ItemType.Folder,
        adapter,
      ),
      searchItemsMP: getAdapterMethod<MediaManagerSDKResponse<MediaManagerSDKFile[]>>(
        SEARCH_METHODS_PATHS,
        source,
        ItemType.File,
        adapter,
      ),
      searchFoldersMP: getAdapterMethod<MediaManagerSDKResponse<MediaManagerSDKFolder[]>>(
        SEARCH_METHODS_PATHS,
        source,
        ItemType.Folder,
        adapter,
      ),
      moveItemsToTrashMP: getAdapter(MainSource.SiteFiles).items.remove,
      getRecommendedCategoriesMP: getAdapter(MainSource.Home).categories.get,
      getRecentFilesMP: getAdapter(MainSource.SiteFiles).items.recent,
      createZipMP: getAdapter(MainSource.SiteFiles).items.createZip,
      updateRecommendedCategoriesMP: getAdapter(MainSource.Home).categories.update,

      restoreItemsFromTrashMP: getAdapter(MainSource.Trash).trash.restore,
      deleteForeverMP: getAdapter(MainSource.Trash).trash.remove,

      copyItemsMP: getAdapter(MainSource.SiteFiles).items.copy,
      moveItemsMP: getAdapter(MainSource.SiteFiles).items.move,
      fetchUserInfoMP: (item: MediaManagerSDKAnyItem) => cachedUserInfoLoader(item.userId),
      updateItemMP: (item, newItemProps, options) => {
        const itemAdapter = getItemAdapter(item);
        const itemType = getItemType(item);
        const itemSource = getItemSourceSafe(item);

        return getAdapterMethod<MediaManagerSDKResponse<MediaManagerSDKFile>>(
          UPDATE_METHODS_PATHS,
          itemSource,
          itemType,
          itemAdapter,
        )(item, newItemProps, options);
      },
      createNewFolderMP: ({ name, mediaType = MediaType.Folder, parentId }) =>
        getAdapterMethod<MediaManagerSDKResponse<MediaManagerSDKFolder>>(
          CREATE_NEW_FOLDER_METHODS_PATHS,
          MainSource.SiteFiles,
          ItemType.Folder,
          adapter,
        )(name, { mediaType, parentId }),
      emptyTrashMP: getAdapter(MainSource.Trash).trash.empty,
      getDownloadUrlMP: (item) =>
        getAdapterMethod<MediaManagerSDKResponse<MediaManagerSDKDownloadResponse>>(
          GET_DOWNLOAD_URL_METHODS_PATHS,
          item.source,
          ItemType.File,
          getAdapter(item.source),
        )(item.id, item.siteMediaToken ? { siteMediaToken: item.siteMediaToken } : null),
      privateItemInfoMP: getAdapter(MainSource.SiteFiles).item.get,
      createNewBoardMP: (board) => getAdapter(MainSource.Boards).items.create(board),
      updateBoardMP: (board) => getAdapter(MainSource.Boards).items.update(board.id, board),
      addItemsToBoardsMP: getAdapter(MainSource.Boards).items.addItems,
      removeBoardsMP: getAdapter(MainSource.Boards).items.remove,
    }),
    [adapter, cachedUserInfoLoader, getAdapter, getItemAdapter],
  );
};
